import React, { useContext, useEffect } from 'react';
import { EventDetailsContext } from '../../context/event_details.context';
import ConfirmationModal from '../confirmation_modal/confirmation_modal';
import ErrorBoundary from '../../../components/react/error_boundary/error_boundary';
import StartDate from '../start_date/start_date';
import StartTime from '../start_time/start_time';
import styles from './header.module.scss';
import EndTime from '../end_time/end_time';
import EndDate from '../end_date/end_date';
import classNames from 'classnames';
import ConfirmInfo from '../confirm_info/confirm_info';
import CtaSection from '../cta_section/cta_section';
import AttendingInfo from '../attending_info/attending_info';

const CLOUDINARY_PATH = 'https://res.cloudinary.com/devex/image/fetch/o_50,c_pad,q_auto,f_auto/';

const Header: React.FC = () => {
  const {
    event,
    isSignedIn,
    hasRegistrationCallbackParam,
    hasCorrectPrivilege,
    setShowConfirmInfo,
  } = useContext(EventDetailsContext);
  const backgroundImagePath = CLOUDINARY_PATH + event?.image_url;

  const renderClosedRegistration: JSX.Element = (
    <span className={styles.closedRegistration} data-testid='closed-registration'>
      Event registration is closed
    </span>
  );

  const renderEventLocation: JSX.Element =
    event?.attendance_type === 'virtual' ? (
      <span className={styles.text} data-testid='online-event'>
        Online
      </span>
    ) : event?.location ? (
      <span className={styles.text} data-testid='event-location'>
        {event.location}
        {event?.attendance_type === 'hybrid' && ' & Online'}
      </span>
    ) : null;

  useEffect(() => {
    if (
      isSignedIn &&
      event?.id &&
      !event?.is_current_user_attending &&
      hasCorrectPrivilege &&
      hasRegistrationCallbackParam
    ) {
      return setShowConfirmInfo(true);
    }
  }, [hasRegistrationCallbackParam, event, hasCorrectPrivilege]);

  return (
    <>
      <ConfirmInfo />
      <ConfirmationModal />
      <div
        style={{
          backgroundImage: `url(${backgroundImagePath})`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
        }}
        className={`text-center ${styles.header}`}
      >
        <p className={styles.text}>{event?.overline}</p>
        <h1 className={`fs-0 fw-bold text-white ${styles.heading}`}>
          {event?.title}
        </h1>
        <p
          className={classNames('mt-2', styles.text, {
            'mb-0': event.singleDayWithTimespan,
          })}
        >
          <ErrorBoundary>
            <StartDate />
          </ErrorBoundary>
          {event.multiDayEvent && (
            <span>
              {' - '}
              <EndDate />
            </span>
          )}
          {' | '}
          {renderEventLocation}
        </p>
        {event.singleDayWithTimespan && (
          <p className={styles.text}>
            <ErrorBoundary>
              <StartTime displayTimezone={false} />
            </ErrorBoundary>
            {' - '}
            <ErrorBoundary>
              <EndTime />
            </ErrorBoundary>
          </p>
        )}
        {<>
          {event?.registration_open ?
            !event?.is_current_user_attending && <CtaSection position='center' />
            : renderClosedRegistration}
          {event?.is_current_user_attending && <AttendingInfo />}
        </>}
      </div>
    </>
  );
};

export default Header;
